import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiX, FiFileText } from 'react-icons/fi';
import api from '../../services/api';
import SiteFieldAuditModal from '../Audit/SiteFieldAuditModal';
import { useProject } from '../../context/ProjectContext';
import Alert from '../Common/Alert';
import MultiSelectDropdown from '../Common/MultiSelectDropdown';
import SearchableSelect from '../Common/SearchableSelect';
import { sites as siteMessages } from '../../utils/validationMessages';
import VisitModalHeader from '../Visits/VisitModalHeader';
import './AddNewSiteModal.css';

const EditSiteModal = ({ site, project, onClose, onSave, initialEditMode = false }) => {
  const { projectInfo } = useProject();
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    siteCode: '',
    investigator: '',
    investigatorEmail: '',
    siteName: '',
    siteEmail: '',
    country: '',
    countryNo: '',
    ecEmail: '',
    recruitmentProjections: '',
    isActive: false,
    cra: null,
    craId: '',
    craIds: [],
  });
  const [isSiteOnboarded, setIsSiteOnboarded] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [investigators, setInvestigators] = useState([]);
  const [cras, setCras] = useState([]);
  const [loadingInvestigators, setLoadingInvestigators] = useState(true);
  const [loadingCRAs, setLoadingCRAs] = useState(true);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showFieldAudit, setShowFieldAudit] = useState(false);
  const [auditFieldName, setAuditFieldName] = useState('');
  /** UI label for the field whose audit button was clicked (shown in Audit Detail "Field Name" column). */
  const [auditFieldDisplayLabel, setAuditFieldDisplayLabel] = useState('');
  const [auditTitle, setAuditTitle] = useState('Audit Detail');
  const initialFormDataRef = useRef(null);

  useEffect(() => {
    fetchInvestigators();
    fetchCRAs();
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await api.get('/countries');
      setCountries(response.data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
      setCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Update edit mode when initialEditMode prop changes
  useEffect(() => {
    setIsEditMode(initialEditMode);
  }, [initialEditMode]);

  // When modal opens with a site, fetch full details from API so Investigator, Country, etc. are always current
  useEffect(() => {
    if (!site?.siteId) return;
    setIsSiteOnboarded(!!(site.isSiteOnboarding ?? site.IsSiteOnboarding));
    let cancelled = false;
    const fetchDetails = async () => {
      try {
        const res = await api.get(`/sites/${site.siteId}/details`);
        if (cancelled || !res.data) return;
        const d = res.data;
        setIsSiteOnboarded(!!(d.isSiteOnboarding ?? d.IsSiteOnboarding));
        const craName = d.cra || null;
        const craIdFromApi = d.craId != null && d.craId !== '' ? String(d.craId) : '';
        const craListFromApi = Array.isArray(d.craList) ? d.craList.map(c => String(c.id)) : (craIdFromApi ? [craIdFromApi] : []);
        const countryName = d.siteAddress || '';
        setFormData(prev => {
          const next = {
            ...prev,
            siteCode: d.siteCode ?? d.vSiteCode ?? '',
            investigator: d.piName || '',
            investigatorEmail: d.investigatorEmail || '',
            siteName: d.siteName || '',
            siteEmail: d.siteEmail || '',
            country: countryName,
            countryNo: '', // set below when countries load by matching countryName
            ecEmail: d.ecEmail || '',
            recruitmentProjections: d.recruitmentProjections != null ? String(d.recruitmentProjections) : '',
            isActive: d.isActive !== undefined ? d.isActive : true,
            cra: craName,
            craId: craIdFromApi,
            craIds: craListFromApi,
          };
          initialFormDataRef.current = next;
          return next;
        });
      } catch (err) {
        if (!cancelled) console.error('Error fetching site details:', err);
        setIsSiteOnboarded(!!(site.isSiteOnboarding ?? site.IsSiteOnboarding));
        // Fallback to prop site data
        setFormData(prev => {
          const next = {
            ...prev,
            siteCode: site.siteCode ?? site.vSiteCode ?? '',
            investigator: site.piName || '',
            investigatorEmail: site.investigatorEmail || '',
            siteName: site.siteName || '',
            siteEmail: site.siteEmail || '',
            country: site.siteAddress || '',
            countryNo: '',
            ecEmail: site.ecEmail || '',
            recruitmentProjections: site.recruitmentProjections != null ? String(site.recruitmentProjections) : (site.recruitmentProjections || ''),
            isActive: site.isActive !== undefined ? site.isActive : true,
            cra: site.cra || null,
            craId: site.craId != null && site.craId !== '' ? String(site.craId) : '',
            craIds: Array.isArray(site.craList) ? site.craList.map(c => String(c.id)) : (site.craId ? [String(site.craId)] : []),
          };
          initialFormDataRef.current = next;
          return next;
        });
      }
    };
    fetchDetails();
    return () => { cancelled = true; };
  }, [site?.siteId]);

  // When countries load, set countryNo from current country name (from details)
  useEffect(() => {
    if (countries.length === 0 || !formData.country) return;
    const found = countries.find(c => (c.countryNiceName || '').trim() === (formData.country || '').trim());
    if (found && formData.countryNo !== String(found.countryNo)) {
      setFormData(prev => {
        const next = { ...prev, countryNo: String(found.countryNo) };
        initialFormDataRef.current = next;
        return next;
      });
    }
  }, [countries, formData.country]);

  const fetchInvestigators = async () => {
    try {
      setLoadingInvestigators(true);
      const response = await api.get('/investigators');
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setInvestigators(list);
    } catch (err) {
      console.error('Error fetching investigators:', err);
      setInvestigators([]);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Could not load investigators.';
      setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Could not load investigators.' });
    } finally {
      setLoadingInvestigators(false);
    }
  };

  const fetchCRAs = async () => {
    try {
      setLoadingCRAs(true);
      const response = await api.get('/users/cras');
      const raw = response.data || [];
      const seen = new Set();
      const crasList = raw
        .map(cra => ({ id: String(cra.id), name: cra.name || '' }))
        .filter(cra => cra.name && !seen.has(cra.id) && seen.add(cra.id));
      setCras(crasList);
    } catch (err) {
      console.error('Error fetching CRAs:', err);
      setCras([]);
    } finally {
      setLoadingCRAs(false);
    }
  };

  const handleInvestigatorChange = (e) => {
    const selectedInvestigatorName = e.target.value;
    setFieldErrors(prev => ({ ...prev, investigator: '' }));
    if (error) setError(null);

    // Find the selected investigator from the already-fetched list
    const selectedInvestigator = investigators.find(
      inv => (inv.fullName || inv.name) === selectedInvestigatorName
    );

    // Set investigator and email from the fetched list
    setFormData(prev => ({
      ...prev,
      investigator: selectedInvestigatorName,
      investigatorEmail: selectedInvestigator?.email || '', // Use email from the list
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Don't handle investigator change here - use handleInvestigatorChange
    if (name === 'investigator') {
      handleInvestigatorChange(e);
      return;
    }

    if (name === 'countryNo') {
      handleCountryChange(e);
      return;
    }
    // No. Of Recruitment Projections: text input, digits only
    if (name === 'recruitmentProjections') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
      if (error) setError(null);
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (name) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (error) setError(null);
  };

  const isValidEmail = (value) => {
    if (!value || typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (!trimmed) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed);
  };

  const normalizeTrim = (v) => (v == null ? '' : String(v)).trim();

  const normalizeRecruitment = (v) => {
    if (v === '' || v == null) return '';
    const n = parseInt(String(v), 10);
    return Number.isNaN(n) ? normalizeTrim(v) : String(n);
  };

  const craIdsEqual = (a, b) => {
    const sa = [...(a || [])].map(String).sort();
    const sb = [...(b || [])].map(String).sort();
    if (sa.length !== sb.length) return false;
    return sa.every((id, i) => id === sb[i]);
  };

  /** True if current form differs from the snapshot taken after details load (before e-sign). */
  const hasEditsComparedToInitial = () => {
    const initial = initialFormDataRef.current;
    if (!initial) return true;
    if (normalizeTrim(formData.siteName) !== normalizeTrim(initial.siteName)) return true;
    if (normalizeTrim(formData.investigator) !== normalizeTrim(initial.investigator)) return true;
    if (normalizeTrim(formData.investigatorEmail) !== normalizeTrim(initial.investigatorEmail)) return true;
    if (normalizeTrim(formData.siteEmail) !== normalizeTrim(initial.siteEmail)) return true;
    if (normalizeTrim(formData.ecEmail) !== normalizeTrim(initial.ecEmail)) return true;
    if (String(formData.countryNo || '') !== String(initial.countryNo || '')) return true;
    if (normalizeRecruitment(formData.recruitmentProjections) !== normalizeRecruitment(initial.recruitmentProjections)) return true;
    if (!!formData.isActive !== !!initial.isActive) return true;
    if (!craIdsEqual(formData.craIds, initial.craIds)) return true;
    if (!isSiteOnboarded && normalizeTrim(formData.siteCode) !== normalizeTrim(initial.siteCode)) return true;
    return false;
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.investigator?.trim()) errs.investigator = siteMessages.investigatorRequired;
    const siteNameTrim = formData.siteName?.trim();
    if (!siteNameTrim) errs.siteName = siteMessages.siteNameRequired;
    else if (siteNameTrim.length < 3) errs.siteName = siteMessages.siteNameMinLength;
    if (!formData.countryNo && !formData.country?.trim()) errs.country = siteMessages.countryRequired;
    if (!isSiteOnboarded && !(formData.siteCode || '').trim()) errs.siteCode = siteMessages.siteCodeRequired;
    if (formData.siteEmail?.trim() && !isValidEmail(formData.siteEmail)) errs.siteEmail = siteMessages.emailInvalid;
    if (formData.ecEmail?.trim() && !isValidEmail(formData.ecEmail)) errs.ecEmail = siteMessages.emailInvalid;
    const rec = formData.recruitmentProjections;
    if (rec !== '' && rec != null) {
      const num = parseInt(rec, 10);
      if (Number.isNaN(num)) errs.recruitmentProjections = siteMessages.recruitmentInvalidNumber;
      else if (num < 0) errs.recruitmentProjections = siteMessages.recruitmentNegative;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCancel = () => {
    // Simply close the modal without resetting or saving
    onClose();
  };

  const handleClear = () => {
    setFormData((prev) => ({
      ...prev,
      siteName: '',
      investigator: '',
      investigatorEmail: '',
      siteEmail: '',
      countryNo: '',
      country: '',
      ecEmail: '',
      recruitmentProjections: '',
      cra: null,
      craId: '',
      craIds: [],
    }));
    setError(null);
    setFieldErrors({});
  };

  const handleCountryChange = (e) => {
    const selectedCountryNo = e.target.value;
    const selectedCountry = countries.find(c => c.countryNo.toString() === selectedCountryNo);
    setFormData(prev => ({
      ...prev,
      countryNo: selectedCountryNo,
      country: selectedCountry ? selectedCountry.countryNiceName : '',
    }));
    setFieldErrors(prev => ({ ...prev, country: '' }));
  };

  const doUpdateSite = async () => {
    const capturedRemark = null;
    const requestData = {
      projectCode: project,
      investigator: formData.investigator,
      investigatorEmail: formData.investigatorEmail || null,
      siteName: formData.siteName,
      siteEmail: formData.siteEmail || null,
      countryNo: formData.countryNo ? parseInt(formData.countryNo, 10) : null,
      ecEmail: formData.ecEmail || null,
      recruitmentProjections: formData.recruitmentProjections ? parseInt(formData.recruitmentProjections) : null,
      isActive: formData.isActive,
      remark: capturedRemark,
      ...(!isSiteOnboarded && { siteCode: (formData.siteCode || '').trim() }),
    };
    const response = await api.post(`/sites/${site.siteId}`, requestData);
    const updatedSite = response.data;

    const initialCraIds = initialFormDataRef.current?.craIds ?? (site.craList && site.craList.map(c => String(c.id))) ?? [];
    const currentCraIds = formData.craIds || [];
    const idsChanged = currentCraIds.length !== initialCraIds.length || currentCraIds.some((id, i) => id !== initialCraIds[i]);
    if (formData.isActive && idsChanged) {
      const remark = capturedRemark ?? '';
      await api.post(`/sites/${site.siteId}/set-cras`, { craIds: currentCraIds, remark });
    }

    const siteData = {
      siteId: updatedSite.siteId,
      siteNo: updatedSite.siteNo,
      siteCode: updatedSite.siteCode ?? formData.siteCode,
      piName: updatedSite.piName,
      siteName: updatedSite.siteName,
      siteAddress: updatedSite.siteAddress || formData.country,
      therapeuticArea: updatedSite.therapeuticArea || '',
      disease: updatedSite.disease || '',
      investigator: formData.investigator,
      investigatorEmail: formData.investigatorEmail,
      siteEmail: formData.siteEmail,
      country: formData.country,
      ecEmail: formData.ecEmail,
      recruitmentProjections: formData.recruitmentProjections,
      isActive: formData.isActive,
      cra: formData.craIds?.length ? formData.craIds.map(id => cras.find(c => c.id === id)?.name).filter(Boolean).join(', ') || formData.cra : updatedSite.cra || site.cra,
    };
    if (onSave) onSave(siteData);
    setIsEditMode(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isEditMode) return;

    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;
    if (!hasEditsComparedToInitial()) {
      setToast({ type: 'error', message: siteMessages.atLeastOneChangeRequired });
      return;
    }
    void runAfterESign();
  };

  const runAfterESign = () => {
    setLoading(true);
    doUpdateSite().catch((err) => {
      const data = err.response?.data;
      const code = data?.code;
      const errorMessage =
        data?.message ||
        data?.error ||
        err.message ||
        'Failed to update site. Please try again.';
      if (code === 'SITE_INACTIVE_BLOCKED_BY_VISITS') {
        setError(null);
        setFormData((prev) => ({ ...prev, isActive: true }));
        setToast({
          type: 'error',
          message: errorMessage || siteMessages.cannotInactiveWithActiveVisits,
        });
        console.error('Error updating site:', err);
        return;
      }
      setError(errorMessage);
      console.error('Error updating site:', err);
    }).finally(() => setLoading(false));
  };

  const investigatorSearchOptions = useMemo(() => {
    const base = investigators.map((inv) => ({
      value: inv.fullName || inv.name,
      label: inv.fullName || inv.name,
    }));
    const curr = (formData.investigator || '').trim();
    if (curr && !investigators.some((inv) => (inv.fullName || inv.name) === curr)) {
      return [...base, { value: curr, label: `${curr} (Current)` }];
    }
    return base;
  }, [investigators, formData.investigator]);

  if (!site) {
    return null;
  }

  const siteNo = site.siteNo ?? site.siteId;
  const auditSiteNo = site.nSiteNo ?? site.siteNo ?? site.siteId;
  const auditSiteNumericId = site.siteId ?? site.SiteId ?? site.nSiteId;
  const auditProjectId = projectInfo?.projectId ?? projectInfo?.ProjectId;
  const auditProjectCode =
    project && project !== 'all' ? project : undefined;

  const handleOpenAudit = (fieldName, uiLabel) => {
    if (!siteNo || !fieldName) return;
    setAuditFieldName(fieldName);
    setAuditFieldDisplayLabel(
      uiLabel != null && String(uiLabel).trim() !== '' ? String(uiLabel).trim() : ''
    );
    setAuditTitle('Audit Detail');
    setShowFieldAudit(true);
  };

  const renderLabel = (label, fieldName, required = false) => (
    <div className="form-label-row">
      <label className="form-label">
        {label}
        {required && <span className="required-asterisk"> *</span>}
      </label>
      {!isEditMode && fieldName && auditSiteNo ? (
        <button
          type="button"
          className="audit-icon-button"
          onClick={() => handleOpenAudit(fieldName, label)}
          title={`View ${label} audit`}
        >
          <FiFileText size={14} />
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      <div className="modal-overlay add-new-site-overlay" style={{ zIndex: 2000 }}>
        <div className="modal-content add-new-site-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{isEditMode ? 'Edit Site' : 'Site Details'}</h3>
            <button className="modal-close" onClick={onClose} disabled={loading}>
              <FiX size={24} />
            </button>
          </div>

          {error && (
            <div className="error-message" style={{
              padding: '12px 24px',
              backgroundColor: '#fee',
              color: '#c00',
              margin: '0 24px',
              borderRadius: '4px',
              border: '1px solid #fcc'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={isEditMode ? handleSubmit : (e) => e.preventDefault()}>
            <div style={{ margin: '0 24px 0 24px' }}>
              <VisitModalHeader
                projectCode={project}
                siteName={site.siteName ?? site.sitedescription ?? formData.siteName}
                siteCode={formData.siteCode || site.siteCode || site.vSiteCode || ''}
                piName={(site.piName ?? site.investigatorDesc ?? formData.investigator) || ''}
              />
            </div>
            <div className="form-grid-3">
              <div className="form-group">
                {renderLabel('Project', 'ProjectCode')}
                <input
                  type="text"
                  className="form-input"
                  value={project || ''}
                  disabled
                />
              </div>

              <div className="form-group">
                {renderLabel('Site Name', 'SiteDescription', true)}
                <input
                  type="text"
                  className="form-input"
                  name="siteName"
                  value={formData.siteName}
                  onChange={handleChange}
                  disabled={!isEditMode}
                />
                {isEditMode && fieldErrors.siteName && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.siteName}</div>
                )}
              </div>

              <div className="form-group">
                {renderLabel('Site Email Id', 'SiteEmailId')}
                <input
                  type="email"
                  className="form-input"
                  name="siteEmail"
                  value={formData.siteEmail}
                  onChange={handleChange}
                  disabled={!isEditMode}
                />
                {isEditMode && fieldErrors.siteEmail && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.siteEmail}</div>
                )}
              </div>




            </div>

            <div className="form-grid-3">
              <div className="form-group">
                {renderLabel('Investigator', 'InvestigatorDesc', true)}
                <SearchableSelect
                  name="investigator"
                  value={formData.investigator}
                  onChange={handleChange}
                  options={investigatorSearchOptions}
                  placeholder={loadingInvestigators ? 'Loading investigators...' : 'Select Investigator'}
                  disabled={!isEditMode || loadingInvestigators}
                  searchPlaceholder="Search by investigator name…"
                  emptyOptionLabel={loadingInvestigators ? 'Loading investigators...' : 'Select Investigator'}
                />
                {isEditMode && fieldErrors.investigator && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.investigator}</div>
                )}
              </div>

              <div className="form-group">
                {renderLabel('Investigator Email Id', 'InvestigatorMail')}
                <input
                  type="email"
                  className="form-input"
                  name="investigatorEmail"
                  value={formData.investigatorEmail || ''}
                  onChange={handleChange}
                  disabled={true}
                  placeholder={formData.investigatorEmail ? '' : 'Not available'}
                  style={{
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div className="form-group">
                {renderLabel('Country', 'CountryNiceName', true)}
                <SearchableSelect
                  name="countryNo"
                  value={formData.countryNo === '' || formData.countryNo == null ? '' : String(formData.countryNo)}
                  onChange={handleChange}
                  options={countries.map((country) => ({
                    value: String(country.countryNo),
                    label: country.countryNiceName,
                  }))}
                  placeholder={loadingCountries ? 'Loading countries...' : 'Select Country'}
                  disabled={!isEditMode || loadingCountries}
                  searchPlaceholder="Search by country name…"
                  emptyOptionLabel={loadingCountries ? 'Loading countries...' : 'Select Country'}
                />
                {isEditMode && fieldErrors.country && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.country}</div>
                )}
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-group">
                {renderLabel('EC Email', 'ECMailId')}
                <input
                  type="email"
                  className="form-input"
                  name="ecEmail"
                  value={formData.ecEmail}
                  onChange={handleChange}
                  disabled={!isEditMode}
                />
                {isEditMode && fieldErrors.ecEmail && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.ecEmail}</div>
                )}
              </div>

              <div className="form-group">
                {renderLabel('No. Of Recruitment Projections', 'RecruitmentProjections')}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-input"
                  name="recruitmentProjections"
                  value={formData.recruitmentProjections}
                  onChange={handleChange}
                  disabled={!isEditMode}
                />
                {isEditMode && fieldErrors.recruitmentProjections && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.recruitmentProjections}</div>
                )}
              </div>

              <div className="form-group">
                {renderLabel('CRA', 'CRA')}
                {isEditMode ? (
                  <>
                    {loadingCRAs ? (
                      <div>Loading CRA...</div>
                    ) : (
                      <MultiSelectDropdown
                        placeholder={formData.isActive ? 'Select CRA' : 'CRA cannot be assigned to an inactive site'}
                        options={cras.map(c => ({ value: c.id, label: c.name }))}
                        selectedValues={formData.craIds || []}
                        onChange={(ids) => {
                          setFormData(prev => ({ ...prev, craIds: ids }));
                          setFieldErrors(prev => ({ ...prev, craIds: '' }));
                        }}
                        disabled={loadingCRAs || !formData.isActive}
                      />
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={formData.cra || (formData.craIds?.length ? formData.craIds.map(id => cras.find(c => c.id === id)?.name).filter(Boolean).join(', ') : '') || 'Not Assigned'}
                    disabled
                    style={{
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed'
                    }}
                  />
                )}
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-group">
                <div className="form-label-row">
                  <label className="form-label checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      disabled={!isEditMode}
                    />
                    Active
                  </label>
                  {!isEditMode && auditSiteNo ? (
                    <button
                      type="button"
                      className="audit-icon-button"
                      onClick={() => handleOpenAudit('IsActive', 'Active')}
                      title="View Active audit"
                    >
                      <FiFileText size={14} />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {isEditMode && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update'}
                </button>
              )}
              {!isEditMode ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Close
                  </button>

                </>
              )}
            </div>
          </form>
        </div>
        <SiteFieldAuditModal
          isOpen={showFieldAudit}
          onClose={() => {
            setShowFieldAudit(false);
            setAuditFieldDisplayLabel('');
          }}
          siteNo={auditSiteNo}
          fieldName={auditFieldName}
          fieldDisplayLabel={auditFieldDisplayLabel}
          title={auditTitle}
          projectId={auditProjectId}
          siteId={auditSiteNumericId}
          projectCode={auditProjectId != null && auditProjectId !== '' ? undefined : auditProjectCode}
          overlayClassName="audit-overlay-top"
        />
      </div>
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </>
  );
};

export default EditSiteModal;
